import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import { DefaultResponseWithData } from "../models/system";
import { IPartPublishResult, IPublishResultsResponse } from "../models/Publishing";

const POLL_MS = 2500;
// Bulk publishes run through a queue that can sit behind other work; stop polling after this
// so a stalled receiver doesn't leave the modal spinning forever. The modal explains the stop.
const MAX_POLL_MS = 10 * 60 * 1000;

/**
 * Polls `GET api/ListingStatus/results/{correlationId}` until every part the action touched has
 * a settled outcome. Pass `initialData` when the caller already has a result (single publish
 * returns one inline) so the modal renders it before the first poll lands.
 */
export default function usePublishResults(
  correlationId: string | null | undefined,
  initialParts?: IPartPublishResult[] | null
) {
  const { get } = useApiHelper();
  const queryClient = useQueryClient();
  const startedAt = useRef<number>(Date.now());

  useEffect(() => {
    startedAt.current = Date.now();
  }, [correlationId]);

  const timedOut = () => Date.now() - startedAt.current > MAX_POLL_MS;

  const results = useQuery({
    queryKey: ["publishResults", correlationId],
    queryFn: () =>
      get<DefaultResponseWithData<IPublishResultsResponse>>(`/listingstatus/results/${correlationId}`),
    enabled: !!correlationId,
    // Fresh state every time the modal opens on a new correlation.
    cacheTime: 0,
    staleTime: 0,
    refetchInterval: (data) => {
      if (!data?.data) return POLL_MS;
      if (data.data.isComplete) return false;
      return timedOut() ? false : POLL_MS;
    },
    initialData:
      correlationId && initialParts
        ? {
            success: true,
            message: "",
            data: buildLocalResponse(correlationId, initialParts),
          }
        : undefined,
    // initialData counts as fresh unless we say otherwise - we want the first real poll straight away.
    initialDataUpdatedAt: 0,
  });

  const isComplete = results.data?.data?.isComplete ?? false;
  const stoppedEarly = !isComplete && !!results.data?.data && timedOut();

  // Once the action settles, the rows behind the tables and the part page are out of date.
  const notifiedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!isComplete || !correlationId || notifiedFor.current === correlationId) return;
    notifiedFor.current = correlationId;

    queryClient.invalidateQueries(["parts"]);
    queryClient.invalidateQueries(["pagedTable"]);
    queryClient.invalidateQueries(["bulkListing"]);
    results.data?.data?.parts.forEach((p) => {
      queryClient.invalidateQueries(["parts", p.partDataId]);
      queryClient.invalidateQueries(["partDetails", p.partDataId]);
      queryClient.invalidateQueries(["listingStatus", p.partDataId]);
    });
  }, [isComplete, correlationId]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    results,
    response: results.data?.data ?? null,
    isComplete,
    stoppedEarly,
  };
}

/** Per-marketplace status for one part - what the pills read. */
export function usePartListingStatus(partId: string | undefined, enabled = true) {
  const { get } = useApiHelper();

  return useQuery({
    queryKey: ["listingStatus", partId],
    queryFn: () => get<DefaultResponseWithData<IPartPublishResult>>(`/listingstatus/part/${partId}`),
    enabled: !!partId && enabled,
    staleTime: 1000 * 30,
  });
}

// Shapes a single inline publish result like the polled response so the modal has one input.
function buildLocalResponse(correlationId: string, parts: IPartPublishResult[]): IPublishResultsResponse {
  const count = (outcome: number) => parts.filter((p) => p.outcome === outcome).length;

  return {
    correlationId,
    isComplete: parts.every((p) => p.marketplaces.every((m) => m.status !== 1)),
    total: parts.length,
    failed: count(0),
    partial: count(1),
    pending: count(2),
    published: count(3),
    parts,
  };
}
